import type { GenerateCompositionPlanResponse } from "./generateCompositionPlan";

import type { DocEditorResponse } from "../types/DocEditorResponse";

import type { JobStatusResponse } from "../types/JobStatusResponse";

import {
  DocOperationJobStatusWithInterval,
  GetMusic,
  SeparateMusicStems,
} from "../components/action";

import {

  EMPTY_GUID,

} from "./constant";

import {

  createUUID,

  getBlobContainer,

  readCookie,

  isUserLoggedIn,

} from "./helper";

import type { SeparateStemsRequest, SeparateStemsResult } from "./separateStems";

import { enqueueMusicJob } from "./jobStatusHelper";
import { readJobTimingFields } from "../types/MusicJobStatus";
import { stemJobCounter } from "./general-config";
import { fetchBlobFromResultPath } from "./musicBlobFetch";
import { fetchMusicMetadataFromBlob, resolveCompositionPlanMetaFilename } from "./compositionPlanBlob";
import {
  resolveStemSeparationDocSource,
  updateMusicDocProfileWithStems,
} from "./musicExport";
import {
  isMusicAudioUrl,
  isStemArchiveUrl,
  parseStemsZipRefFromResultUrl,
  requireDocOperationResultUrl,
} from "./jobResultBlob";
import { getVisitGuidForTracking } from "@/utils/visitor";
import {
  buildApiMusicPrompt,
  resolveMusicGenerationDurationMs,
} from "./musicGenerationPrompt";
import { applyLatinGenreLanguageToPrompt } from "./onboardingData";
import {
  getMusicGenerationCustomData,
  registerMusicGenerationCustomData,
} from "./musicEditorQuery";
import {
  buildPersistedEditorCustomData,
  mergePostGenerationCustomData,
} from "./musicCustomData";
import {
  buildMusicGenerationFilename,
  resolveAudioExtensionFromPath,
  audioMimeTypeForFormat,
} from "./musicDisplay";
import {
  isMusicJobGuid,
  resolveMusicJobIdFromBlobFileName,
} from "./musicStorage";

import type {
  GenerateMusicLyricsSection,
  GenerateMusicTimestamp,
} from "./generateMusic";

function buildMusicJobTag(

  prompt?: string,

  compositionPlan?: GenerateCompositionPlanResponse,

): string {

  if (prompt?.trim()) {

    return prompt.trim().slice(0, 80) || "music-generation";

  }

  const sectionName = compositionPlan?.sections[0]?.section_name;

  if (sectionName) {

    return sectionName.slice(0, 80);

  }

  return "music-generation";

}



function getTrackingContext() {

  const visitId = getVisitGuidForTracking();

  const visitorId =

    readCookie("visitorGuid") ??

    readCookie("visitorid") ??

    readCookie("visitorId") ??

    EMPTY_GUID;



  return { visitId, visitorId };

}



function buildGeneralContext() {

  const { visitId, visitorId } = getTrackingContext();

  return {

    visitId,

    visitorId,

    aBTestResult: "",

  };

}



function resolveEditorMusicPrompt(options: {

  prompt?: string;

  compositionPlan?: GenerateCompositionPlanResponse;

}): string {

  if (options.compositionPlan?.sections?.length) {

    const lyrics = options.compositionPlan.sections

      .flatMap((section) => section.lines ?? [])

      .join("\n")

      .trim();

    if (lyrics) {

      return lyrics;

    }

  }

  if (options.prompt?.trim()) {

    return options.prompt.trim();

  }

  return "";

}



/** Matches WorkSimpli.DocEditor.Api MusicGenerationRequest (flat body, not wrapped). */
export function buildMusicGenerationRequest(options: {
  jobId: string;
  blobContainer: string;
  prompt?: string;
  compositionPlan?: GenerateCompositionPlanResponse;
  musicLen?: number;
  forcedInstrumental: boolean;
  guidedSelections?: Record<string, string[]>;
}) {
  const generalContext = buildGeneralContext();
  const editorPrompt = applyLatinGenreLanguageToPrompt(
    resolveEditorMusicPrompt(options),
    options.guidedSelections,
  );
  const musicLengthMs = resolveMusicGenerationDurationMs(options.musicLen);
  const apiPrompt = editorPrompt
    ? buildApiMusicPrompt(editorPrompt, musicLengthMs, isUserLoggedIn())
    : "";

  const lyricsSections = options.compositionPlan?.sections?.map((section) => ({
    section_name: section.section_name,
    lines: section.lines ?? [],
    duration_ms: section.duration_ms,
  }));

  const customData = buildPersistedEditorCustomData({
    prompt: options.prompt,
    guidedSelections: options.guidedSelections,
    lyricsSections,
  });

  const payload: Record<string, unknown> = {
    jobId: options.jobId,
    prompt: apiPrompt,
    musicLength: musicLengthMs,
    forcedInstrumental: options.forcedInstrumental,
    blobOutputFolder: options.blobContainer,
    generalContext: {
      visitId: generalContext.visitId,
      visitorId: generalContext.visitorId,
      aBTestResult: generalContext.aBTestResult,
    },
  };

  return { payload, customData };
}

export function buildMusicGenerationPayload(
  options: Parameters<typeof buildMusicGenerationRequest>[0],
) {
  return buildMusicGenerationRequest(options).payload;
}



/** Matches WorkSimpli.DocEditor.Api StemSeparationRequest (flat body). */
export function buildStemSeparationPayload(request: {
  stemJobId: string;
  docId: string;
  fileName: string;
  musicJobId?: string;
}) {
  const generalContext = buildGeneralContext();
  const sourceJobId =
    (request.musicJobId && isMusicJobGuid(request.musicJobId)
      ? request.musicJobId
      : resolveMusicJobIdFromBlobFileName(request.fileName)) ?? request.stemJobId;

  return {
    jobId: request.stemJobId,
    sourceJobId,
    docId: request.docId,
    fileName: request.fileName,
    blobOutputFolder: getBlobContainer(),
    generalContext: {
      visitId: generalContext.visitId,
      visitorId: generalContext.visitorId,
      aBTestResult: generalContext.aBTestResult,
    },
  };
}



function normalizeDocEditorResponse(

  response: DocEditorResponse & {

    DocId?: string;

    IsRequestSubmitted?: boolean;

    PossibleFailureReason?: string;

  },

): DocEditorResponse {

  return {

    ...response,

    docId: response.docId ?? response.DocId ?? "",

    isRequestSubmitted:

      response.isRequestSubmitted ?? response.IsRequestSubmitted ?? false,

    possibleFailureReason:

      response.possibleFailureReason ?? response.PossibleFailureReason ?? "",

  };

}



function assertDocEditorSubmission(

  response: DocEditorResponse,

  operationLabel: string,

): string {

  const normalized = normalizeDocEditorResponse(

    response as DocEditorResponse & {

      DocId?: string;

      IsRequestSubmitted?: boolean;

      PossibleFailureReason?: string;

    },

  );



  if (

    normalized.validationResponse &&

    normalized.validationResponse.isValidRequest === false

  ) {

    const reasons =

      normalized.validationResponse.failedRulesMessage?.join(", ") ||

      normalized.possibleFailureReason ||

      "Unknown validation error";

    throw new Error(`${operationLabel} request rejected: ${reasons}`);

  }



  if (!normalized.isRequestSubmitted) {

    const reasons =

      normalized.validationResponse?.failedRulesMessage?.join(", ") ||

      normalized.possibleFailureReason ||

      "Unknown error";

    throw new Error(`${operationLabel} request rejected: ${reasons}`);

  }



  if (!normalized.docId || normalized.docId === EMPTY_GUID) {

    throw new Error(`${operationLabel} did not return a valid job id.`);

  }



  return normalized.docId;

}



/** Poll Job Manager until status 30. */
async function pollCompletedJob(
  jobId: string,
  signal?: AbortSignal,
  maxAttempts?: number,
): Promise<JobStatusResponse> {
  const jobResult = await DocOperationJobStatusWithInterval(
    jobId,
    signal,
    maxAttempts,
  );

  if (jobResult.status !== 30) {
    throw new Error("Job did not complete successfully.");
  }

  return jobResult;
}



export async function generateMusicViaDocEditor(options: {

  prompt?: string;

  compositionPlan?: GenerateCompositionPlanResponse;

  musicLen?: number;

  forcedInstrumental: boolean;

  guidedSelections?: Record<string, string[]>;

  signal?: AbortSignal;

  onJobSubmitted?: () => void;

}): Promise<{

  musicId: string;

  filename: string;

  audioBlob: Blob;

  streamMime: string;

  blobContainer: string;

  blobPath?: string;

  lyricsSections: GenerateMusicLyricsSection[] | null;

  timestamps: GenerateMusicTimestamp[] | null;

  metadataFileName?: string;
}> {

  const musicId = createUUID();

  const blobContainer = getBlobContainer();

  const storageTimestamp = Date.now();

  const jobTag = buildMusicJobTag(options.prompt, options.compositionPlan);



  await enqueueMusicJob(musicId, jobTag);



  const { payload, customData } = buildMusicGenerationRequest({
    jobId: musicId,
    blobContainer,
    prompt: options.prompt,
    compositionPlan: options.compositionPlan,
    musicLen: options.musicLen,
    forcedInstrumental: options.forcedInstrumental,
    guidedSelections: options.guidedSelections,
  });

  registerMusicGenerationCustomData(musicId, customData);

  const docEditorResponse = normalizeDocEditorResponse(

    await GetMusic(payload) as DocEditorResponse & {

      DocId?: string;

      IsRequestSubmitted?: boolean;

      PossibleFailureReason?: string;

    },

  );

  assertDocEditorSubmission(docEditorResponse, "Music generation");

  options.onJobSubmitted?.();



  const jobResult = await pollCompletedJob(docEditorResponse.docId, options.signal);
  const { timeTakenInSec } = readJobTimingFields(jobResult);
  if (timeTakenInSec !== undefined) {
    console.info(
      `[music-generation] Job ${docEditorResponse.docId} completed in ${timeTakenInSec}s`,
    );
  }
  const audioResultUrl = requireDocOperationResultUrl(
    jobResult,
    isMusicAudioUrl,
    "Music generation",
  );
  const generatedFormat = resolveAudioExtensionFromPath(audioResultUrl) ?? "wav";
  const displayFilename = buildMusicGenerationFilename(generatedFormat, storageTimestamp);
  const audioBlob = await fetchBlobFromResultPath(audioResultUrl);
  const blobPath = audioResultUrl;
  const filename = displayFilename;

  let lyricsSections: GenerateMusicLyricsSection[] | null = null;
  let timestamps: GenerateMusicTimestamp[] | null = null;
  let metadataFileName: string | undefined = undefined;
  try {
    metadataFileName = resolveCompositionPlanMetaFilename(jobResult);
    const metadata = await fetchMusicMetadataFromBlob(jobResult);
    lyricsSections = metadata.lyricsSections;
    timestamps = metadata.timestamps;
    registerMusicGenerationCustomData(
      musicId,
      mergePostGenerationCustomData(
        getMusicGenerationCustomData(musicId),
        metadata.customData,
      ),
    );
  } catch (error) {
    console.error("Failed to load composition plan lyrics:", error);
  }

  return {

    musicId,

    filename,

    audioBlob,

    streamMime: audioMimeTypeForFormat(generatedFormat),

    blobContainer,

    blobPath,

    lyricsSections,

    timestamps,

    metadataFileName,
  };

}



export async function separateStemsViaDocEditor(
  request: SeparateStemsRequest,
  signal?: AbortSignal,
): Promise<SeparateStemsResult> {
  const stemJobId = createUUID();
  const jobTag = `stems-${request.musicJobId}`;

  await enqueueMusicJob(stemJobId, jobTag);

  const docSource = await resolveStemSeparationDocSource(request.musicJobId);

  const payload = buildStemSeparationPayload({
    stemJobId,
    docId: docSource.docId,
    fileName: docSource.fileName,
    musicJobId: isMusicJobGuid(request.musicJobId)
      ? request.musicJobId
      : resolveMusicJobIdFromBlobFileName(docSource.fileName),
  });
  const docEditorResponse = normalizeDocEditorResponse(
    await SeparateMusicStems(payload) as DocEditorResponse & {
      DocId?: string;
      IsRequestSubmitted?: boolean;
      PossibleFailureReason?: string;
    },
  );
  assertDocEditorSubmission(docEditorResponse, "Stem separation");

  const jobResult = await pollCompletedJob(
    docEditorResponse.docId,
    signal,
    stemJobCounter,
  );
  const { timeTakenInSec } = readJobTimingFields(jobResult);
  if (timeTakenInSec !== undefined) {
    console.info(
      `[stem-separation] Job ${docEditorResponse.docId} completed in ${timeTakenInSec}s`,
    );
  }
  const stemsResultUrl = requireDocOperationResultUrl(
    jobResult,
    isStemArchiveUrl,
    "Stem separation",
  );
  const stemsZipRef = parseStemsZipRefFromResultUrl(
    stemsResultUrl,
    getBlobContainer(),
  );
  try {
    await updateMusicDocProfileWithStems(request.musicJobId, stemsZipRef.filename);
  } catch (error) {
    console.error("Failed to update doc profile with stems:", error);
  }

  return {
    stemJobId,
    filename: stemsZipRef.filename,
    status: "ready",
    stemsZipRef,
    stemsZipName: stemsZipRef.filename,
  };
}


